/**
 * Reset Password Page
 *
 * Set new password using reset token.
 */

import { useState } from 'react'
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom'
import { FiEye, FiEyeOff, FiCheckCircle, FiArrowLeft, FiAlertCircle } from 'react-icons/fi'
import { authApi, isValidPassword } from '../../api'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Alert, AlertTitle } from '../../components/ui/alert'

export function ResetPassword() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  // Validation
  const errors: Record<string, string> = {}

  if (touched.newPassword) {
    const passwordCheck = isValidPassword(formData.newPassword)
    if (!formData.newPassword) {
      errors.newPassword = 'Password is required'
    } else if (!passwordCheck.valid) {
      errors.newPassword = passwordCheck.message!
    }
  }

  if (touched.confirmPassword) {
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password'
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match'
    }
  }

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setError(null)
  }

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched({ newPassword: true, confirmPassword: true })
    setError(null)

    if (!token) {
      setError('Invalid reset link')
      return
    }

    if (Object.keys(errors).length > 0) return
    if (!formData.newPassword || !formData.confirmPassword) return

    setIsLoading(true)

    try {
      const response = await authApi.resetPassword({
        token,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      })

      if (response.success) {
        setIsSuccess(true)
      } else {
        setError(response.error ?? 'Failed to reset password')
      }
    } catch {
      setError('An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4">
        <div className="w-full max-w-md p-8 bg-background rounded-[var(--radius-card)] border border-border shadow-sm text-center">
          <div className="flex flex-col gap-6 items-center">
            <div className="w-16 h-16 rounded-full bg-success flex items-center justify-center">
              <FiCheckCircle className="w-8 h-8 text-white" />
            </div>

            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-bold text-foreground">
                Password reset!
              </h1>
              <p className="text-foreground-muted">
                Your password has been successfully changed. You can now sign in with your new
                password.
              </p>
            </div>

            <Button
              className="w-full"
              onClick={() => navigate('/login')}
            >
              Sign in
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // No token state
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4">
        <div className="w-full max-w-md p-8 bg-background rounded-[var(--radius-card)] border border-border shadow-sm text-center">
          <div className="flex flex-col gap-6 items-center">
            <Alert variant="destructive">
              <FiAlertCircle className="h-4 w-4" />
              <AlertTitle>Invalid or missing reset token</AlertTitle>
            </Alert>

            <p className="text-foreground-muted">
              This password reset link is invalid or has expired. Please request a new one.
            </p>

            <RouterLink to="/forgot-password">
              <Button variant="outline">
                Request new link
              </Button>
            </RouterLink>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4">
      <div className="w-full max-w-md p-8 bg-background rounded-[var(--radius-card)] border border-border shadow-sm">
        <div className="flex flex-col gap-6">
          {/* Back Link */}
          <RouterLink
            to="/login"
            className="text-foreground-muted text-sm flex items-center gap-1 hover:text-foreground"
          >
            <FiArrowLeft />
            Back to login
          </RouterLink>

          {/* Header */}
          <div className="flex flex-col gap-2 text-center">
            <h1 className="text-2xl font-bold text-foreground">
              Set new password
            </h1>
            <p className="text-foreground-muted">
              Choose a strong password for your account.
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <FiAlertCircle className="h-4 w-4" />
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-4">
              {/* New Password */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">New Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.newPassword}
                    onChange={(e) => handleChange('newPassword', e.target.value)}
                    onBlur={() => handleBlur('newPassword')}
                    placeholder="Enter new password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-foreground-muted hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                </div>
                <p className="text-sm text-foreground-muted">
                  8+ characters, uppercase, lowercase, and a number
                </p>
                {errors.newPassword && <p className="text-sm text-error">{errors.newPassword}</p>}
              </div>

              {/* Confirm Password */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Confirm Password</label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  onBlur={() => handleBlur('confirmPassword')}
                  placeholder="Confirm new password"
                />
                {errors.confirmPassword && <p className="text-sm text-error">{errors.confirmPassword}</p>}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Resetting...' : 'Reset password'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
